import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, FileText, Download, AlertCircle, Plus, Mail, Zap, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../foundation/altzero_base/base/components/ui/select';
import { RetailerPromptPicker } from '../common/RetailerPromptPicker';
import { toast } from '../../../../foundation/altzero_base/base/hooks/use-toast';
import { billEmailService } from '../../services/billEmailService';
import { siteService } from '../../services/siteService';
import { billService } from '../../services/billService';
import { auditService } from '../../services/auditService';
import type { InboxEmail, InboxAttachment, HeldReason } from '../../services/billEmailService';
import type { BvSite } from '../../types/billValidationTypes';
import type { BvBill } from '../../types/billTypes';
import { isBrowserRenderable, describeAttachmentType } from '../../utils/attachmentPreview';
import { nmiBase10 } from '../../utils/nmiIdentity';

// Combined View + Assign, split-screen:
//   left  — invoice PDF (big, so the user can read bill period straight off it)
//           + collapsible email message
//   right — assign panel: detected-NMI site suggestions, site search,
//           bill period select / create (dates typed while reading the PDF)

// Why an invoice is still sitting here. An invoice the AI read completely
// assigns itself and parses at ingest, so a held email is always waiting on one
// of these — phrased as the thing the person needs to do about it.
const HELD_REASON_TEXT: Record<HeldReason, string> = {
  nmi: 'No NMI was found on the invoice — pick the site below.',
  multiple_nmis: 'The invoice bills several NMIs — assign each site separately.',
  site: 'The NMI on the invoice matches no site in your organisation.',
  period: 'No billing period was found on the invoice — set it below.',
  retailer: 'The retailer could not be matched — choose one so the right parsing prompt is used.',
  not_enrolled: 'The site is not Enrolled for this billing period.',
};

type Props = {
  open: boolean;
  onClose: () => void;
  email: InboxEmail | null;
  onAssigned: () => void;
};

type Detail = Omit<InboxEmail, 'attachments'> & { bodyHtml: string | null; bodyText: string | null; attachments: InboxAttachment[] };

type NmiSuggestion = { nmi: string; site: BvSite | null };

// One editable row per detected section of a consolidated invoice (Phase 2).
type SectionRow = {
  nmi: string;
  site: BvSite | null;   // resolved from the NMI at open (null = no matching site)
  siteId: string;        // chosen site — defaults to the resolved site's id
  from: string;          // bill_from (yyyy-mm-dd for the date input)
  to: string;            // bill_to
};

function fmtSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function EmailPreviewDrawer({ open, onClose, email, onAssigned }: Props) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeAttachmentId, setActiveAttachmentId] = useState<string | null>(null);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [attachmentMime, setAttachmentMime] = useState<string | null>(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [showEmailBody, setShowEmailBody] = useState(false);

  // Assign form state
  const [siteSearch, setSiteSearch] = useState('');
  const [sites, setSites] = useState<BvSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [siteId, setSiteId] = useState('');
  const [bills, setBills] = useState<BvBill[]>([]);
  const [billId, setBillId] = useState('');
  const [creatingBill, setCreatingBill] = useState(false);
  const [newFrom, setNewFrom] = useState('');
  const [newTo, setNewTo] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [nmiSuggestions, setNmiSuggestions] = useState<NmiSuggestion[]>([]);
  const [reindexing, setReindexing] = useState(false);

  // Consolidated-invoice sections (Phase 2). A multi-section invoice replaces the
  // single site + period form with one row per site it covers.
  const [sectionRows, setSectionRows] = useState<SectionRow[]>([]);
  const [sectionsResolving, setSectionsResolving] = useState(false);
  const isMultiSection = (email?.detected_sections?.length ?? 0) > 1;

  useEffect(() => {
    if (!open || !email) return;
    setLoading(true);
    setDetail(null);
    setActiveAttachmentId(null);
    setAttachmentUrl(null);
    setAttachmentMime(null);
    setShowEmailBody(false);
    setSiteId(email.site_id ?? '');
    setBillId(email.bill_id ?? '');
    setSiteSearch('');
    setCreatingBill(false);
    setAssignError(null);
    setNmiSuggestions([]);
    setSectionRows([]);
    billEmailService.getInboxItem(email.id).then(({ success, data, error: err }) => {
      if (success && data) {
        setDetail(data);
        setError(null);
        const first = data.attachments[0];
        if (first) setActiveAttachmentId(first.id);
        // Default tab: invoice when there's an attachment, else the email body.
        setShowEmailBody(!first);
      } else {
        setError(err ?? 'Failed to load email');
      }
      setLoading(false);
    });
  }, [open, email]);

  // Detected-NMI site suggestions: the sync scans the email/PDF text for NMIs —
  // resolve each to a site so assignment is one click instead of a search.
  useEffect(() => {
    if (!open || !email) return;
    const nmis = (email.detected_nmis ?? []).slice(0, 5);
    if (nmis.length === 0) return;
    let mounted = true;
    Promise.all(
      nmis.map(async (nmi): Promise<NmiSuggestion> => {
        // Searched on the 10-character BASE, not the NMI as printed.
        //
        // bv_sites_list matches `s.nmi ILIKE '%' || p_search || '%'`, so the
        // search term has to be no longer than what is stored. An invoice
        // printing the checksum digit ("QB049857887") therefore found nothing
        // against a site stored without it ("QB04985788") — the needle was
        // longer than the haystack — and the drawer said "no matching site"
        // about a site sitting right there.
        //
        // The base matches BOTH: it is a prefix of the 11-character form and
        // equal to the 10-character one. Same rule the server matches on, which
        // is the point — the chip and the auto-assign must not disagree about
        // whether a site exists.
        const { success, data } = await siteService.list({ search: nmiBase10(nmi), size: 1 });
        return { nmi, site: success ? (data?.sites?.[0] ?? null) : null };
      }),
    ).then((results) => { if (mounted) setNmiSuggestions(results); });
    return () => { mounted = false; };
  }, [open, email]);

  // Resolve each consolidated-invoice section's NMI to a site so the assign panel
  // can list every covered site and pre-select the ones that matched.
  useEffect(() => {
    if (!open || !email) return;
    const secs = email.detected_sections ?? [];
    if (secs.length <= 1) { setSectionRows([]); return; }
    let mounted = true;
    setSectionsResolving(true);
    Promise.all(
      secs.map(async (s): Promise<SectionRow> => {
        const { success, data } = await siteService.list({ search: s.nmi, size: 1 });
        const site = success ? (data?.sites?.[0] ?? null) : null;
        return {
          nmi: s.nmi,
          site,
          siteId: site?.id ?? '',           // pre-select the resolved site
          from: s.period_from ?? '',
          to: s.period_to ?? '',
        };
      }),
    ).then((rows) => { if (mounted) { setSectionRows(rows); setSectionsResolving(false); } });
    return () => { mounted = false; };
  }, [open, email]);

  useEffect(() => {
    if (!open || !email || !activeAttachmentId) return;
    let revoke: string | null = null;
    setAttachmentLoading(true);
    setAttachmentError(null);
    setAttachmentUrl(null);
    setAttachmentMime(null);
    billEmailService.fetchAttachmentBlob(email.id, activeAttachmentId).then(({ success, data, error: err }) => {
      setAttachmentLoading(false);
      if (success && data) {
        const url = URL.createObjectURL(data);
        revoke = url;
        setAttachmentUrl(url);
        // The blob type comes from the response Content-Type, which the server
        // resolves against the filename — trust it over the stored mime_type.
        setAttachmentMime(data.type || null);
      } else {
        setAttachmentError(err ?? 'Failed to load attachment');
      }
    });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [open, email, activeAttachmentId]);

  const loadSites = useCallback(async (search: string) => {
    setSitesLoading(true);
    const { success, data } = await siteService.list({ search, size: 20 });
    if (success) setSites(data?.sites ?? []);
    setSitesLoading(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => loadSites(siteSearch), 250);
    return () => clearTimeout(t);
  }, [open, siteSearch, loadSites]);

  useEffect(() => {
    if (!siteId) {
      setBills([]);
      return;
    }
    billService.listBySite(siteId).then(({ success, data }) => {
      if (!success) return;
      const list = data ?? [];
      setBills(list);

      // ── Put the invoice's own period where it belongs ──────────────────────
      // The dates were read off the invoice at ingest. Leaving the picker empty
      // made a person re-read the PDF and retype what we already knew, on every
      // single email.
      //
      // Only ever a starting point, never a decision: an existing period is
      // PRE-SELECTED and the create form is PRE-FILLED, but nothing is saved
      // until Assign is pressed, so a wrong reading costs one correction rather
      // than a wrong bill.
      //
      // Skipped once the email already has a bill, or once the user has picked
      // or typed something — their choice must not be overwritten underneath
      // them when this re-runs.
      const from = email?.detected_period_from;
      const to = email?.detected_period_to;
      if (!from || !to) return;

      setBillId((currentBillId) => {
        if (currentBillId) return currentBillId;

        // An overlapping period IS this invoice's period — the same billing run
        // is often stored a day or two either side of what the invoice prints.
        // Same rule the server uses when it assigns one of these by itself.
        const overlapping = list.find((b) => b.bill_from <= to && b.bill_to >= from);
        if (overlapping) return overlapping.id;

        // Nothing to attach to, so the only way forward is a new period —
        // opened with the invoice's dates already in it, ready to create.
        setCreatingBill(true);
        setNewFrom((v) => v || from);
        setNewTo((v) => v || to);
        return '';
      });
    });
  }, [siteId, email?.detected_period_from, email?.detected_period_to]);

  /**
   * Pre-select the retailer the AI matched off the invoice.
   *
   * It decides which parsing prompt the invoice is read with, so getting it
   * pre-selected is not cosmetic — an unset retailer silently falls back to the
   * standard prompt instead of the one tuned for that retailer.
   *
   * Only when the AI matched exactly one retailer. An ambiguous or absent match
   * leaves the dropdown alone for a person to choose.
   */
  useEffect(() => {
    if (!open || !email) return;
    const matched = email.ai_extraction;
    if (matched?.retailer_match === 'exact' && matched.retailer_id) {
      setRetailerId(matched.retailer_id);
    }
  }, [open, email]);

  const fmtPeriod = (b: BvBill) =>
    `${new Date(b.bill_from).toLocaleDateString('en-AU')} – ${new Date(b.bill_to).toLocaleDateString('en-AU')}`;

  const pickSuggestedSite = (s: BvSite) => {
    setSiteId(s.id);
    setBillId('');
    // Make sure the picked site is in the dropdown options.
    setSites((prev) => (prev.some((x) => x.id === s.id) ? prev : [s, ...prev]));
  };

  const handleCreateBill = async () => {
    if (!siteId || !newFrom || !newTo) return;
    setAssigning(true);
    setAssignError(null);
    const { success, data, error: err } = await billService.create({
      site_id: siteId,
      bill_from: newFrom,
      bill_to: newTo,
      peak_kwh: '',
      shoulder_kwh: '',
      off_peak_kwh: '',
      feed_in_kwh: '',
      peak_demand_kva: '',
      notes: '',
    });
    setAssigning(false);
    if (!success || !data) {
      setAssignError(err ?? 'Failed to create bill period');
      return;
    }
    setBills((prev) => [data, ...prev]);
    setBillId(data.id);
    setCreatingBill(false);
  };

  // undefined = don't touch whatever retailer is already on the bill.
  const [retailerId, setRetailerId] = useState<string | null | undefined>(undefined);

  // One email, several invoices (ActewAGL: Jul, Aug, Oct for two accounts).
  const [splitting, setSplitting] = useState(false);
  const [splitNotice, setSplitNotice] = useState<string | null>(null);
  const handleSplit = async () => {
    if (!email) return;
    setSplitting(true);
    setAssignError(null);
    setSplitNotice(null);
    const { success, data, error: err } = await billEmailService.splitEmail(email.id);
    setSplitting(false);
    if (!success || !data) { setAssignError(err ?? 'Could not split this email'); return; }
    onAssigned();
    // Nothing left on this email to assign: the invoices are items of their own now.
    if (data.emailIgnored) { onClose(); return; }
    setSplitNotice(data.reason);
    const refreshed = await billEmailService.getInboxItem(email.id);
    if (refreshed.success && refreshed.data) setDetail(refreshed.data);
  };

  /**
   * Re-read this email's invoice with AI and reload the drawer.
   *
   * `onAssigned` is reused to refresh the list behind: the re-read rewrites the
   * detected meter and period, which are both columns the inbox renders, so a
   * stale list would disagree with the drawer it was opened from.
   */
  const handleReindex = async () => {
    if (!email) return;
    setReindexing(true);
    setAssignError(null);
    const { success, data, error: err } = await billEmailService.reindex(email.id);
    setReindexing(false);

    if (!success) {
      setAssignError(err ?? 'Could not re-read this invoice');
      return;
    }
    // A scanned invoice has no text to read. Saying so beats a silent success
    // that leaves the same values on screen and no explanation.
    if (data?.pdfTextEmpty) {
      setAssignError('This invoice is a scan with no readable text, so nothing could be extracted from it.');
      return;
    }

    // Everything was on the invoice, so it assigned itself and is parsing —
    // there is nothing left in this drawer for anyone to do.
    if (data?.assigned) {
      onAssigned();
      onClose();
      return;
    }

    // It read the invoice but something is still missing. Stay open with the
    // values it DID find already filled in, and say what is outstanding, rather
    // than closing and making them find this email again.
    //
    // The detail is re-fetched first: the re-read rewrote the detected meter
    // numbers and the extraction on the server, and without this the drawer
    // would go on showing the values it opened with while displaying a reason
    // produced by the new ones.
    const refreshed = await billEmailService.getInboxItem(email.id);
    if (refreshed.success && refreshed.data) setDetail(refreshed.data);

    onAssigned();
    if (data?.reason) setAssignError(data.reason);
  };

  // The assignment stood but its parse could not be queued: the drawer closes, so say so in a toast.
  const warnIfParseNotQueued = (data?: { parseQueued?: boolean; warning?: string }) => {
    if (data?.parseQueued !== false) return;
    toast({
      title: 'Assigned, but parsing has not started',
      description: data.warning ?? 'The parse could not be queued. It will be picked up automatically shortly.',
    });
  };

  const handleAssign = async () => {
    if (!email || !siteId || !billId) return;
    setAssigning(true);
    setAssignError(null);
    const { success, data, error: err } = await billEmailService.assign(email.id, siteId, billId, retailerId);
    setAssigning(false);
    if (!success) {
      setAssignError(err ?? 'Failed to assign');
      return;
    }
    warnIfParseNotQueued(data);

    // Which invoice got attached to which bill period is a judgement call made by
    // a person reading a PDF — record who made it (migration 066).
    const site = sites.find((s) => s.id === siteId);
    const bill = bills.find((b) => b.id === billId);
    void auditService.log({
      entity_type: 'bill',
      entity_id:   billId,
      site_id:     siteId,
      action:      'assigned',
      field:       'invoice_email',
      new_value:   detail?.subject ?? email.subject ?? null,
      note: `Invoice email assigned to ${site?.nmi ?? 'site'}${bill ? ` · ${fmtPeriod(bill)}` : ''}`,
    });

    onAssigned();
    onClose();
  };

  const resolvedSectionCount = sectionRows.filter((r) => r.siteId).length;

  const handleAssignSections = async () => {
    if (!email) return;
    const anyResolved = sectionRows.some((r) => r.siteId && r.from && r.to);
    if (!anyResolved) return;
    setAssigning(true);
    setAssignError(null);
    const { success, data, error: err } = await billEmailService.assignSections(
      email.id,
      sectionRows.map((r) => ({
        nmi: r.nmi,
        site_id: r.siteId || null,
        bill_from: r.from || null,
        bill_to: r.to || null,
      })),
    );
    setAssigning(false);
    if (!success) {
      setAssignError(err ?? 'Failed to assign');
      return;
    }
    warnIfParseNotQueued(data);
    onAssigned();
    onClose();
  };

  const activeAttachment = detail?.attachments.find((a) => a.id === activeAttachmentId) ?? null;
  const parseFailed = detail?.attachments.some((a) => a.parsing_status === 'failed') ?? false;
  const parsePending = detail?.attachments.some((a) => a.parsing_status === 'pending') ?? false;
  const parseErrors = (detail?.attachments ?? [])
    .filter((a) => a.parsing_status === 'failed' && a.parse_error)
    .map((a) => ({ id: a.id, filename: a.filename, error: a.parse_error as string }));
  const alreadyAssigned = detail?.status === 'matched_manual';
  const pdfCount = (detail?.attachments ?? []).filter((a) => /\.pdf$/i.test(a.filename) || a.mime_type === 'application/pdf').length;

  // What the AI could not resolve, and what it did read.
  //
  // `detail` is preferred over the `email` prop because a re-read rewrites this
  // on the server, and the prop is a snapshot the list handed us when the
  // drawer opened. Reading the prop showed the OLD junk meter numbers and the
  // "by text matching" badge next to a reason that had just been produced by
  // the new extraction — the screen contradicting itself.
  //
  // Emails ingested before AI extraction existed have none, and show neither.
  const extraction = detail?.ai_extraction ?? email?.ai_extraction ?? null;
  const heldReasons = extraction?.held_for ?? [];
  const aiFound = [
    extraction?.nmi ? `NMI ${extraction.nmi}` : null,
    extraction?.period_from && extraction?.period_to
      ? `${extraction.period_from} – ${extraction.period_to}`
      : null,
    extraction?.retailer_name,
    extraction?.account_number ? `acct ${extraction.account_number}` : null,
    extraction?.invoice_number ? `inv ${extraction.invoice_number}` : null,
  ].filter((v): v is string => !!v);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="email-preview" className="max-w-[96vw] w-[96vw] h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b shrink-0">
          <DialogTitle data-testid="email-preview-title" className="text-base">
            {detail?.subject || email?.subject || 'Invoice email'}
            <span className="ml-2 text-xs font-normal text-gray-500">
              {detail ? `${detail.sender_name ? `${detail.sender_name} · ` : ''}${detail.sender_email} · ${new Date(detail.received_at).toLocaleString('en-AU')}` : ''}
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div data-testid="email-preview-loading" className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : error ? (
          <div data-testid="email-preview-error" className="flex-1 flex items-center justify-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        ) : detail && (
          <div className="flex-1 flex min-h-0">

            {/* ── Left: Email / Invoice tabs ────────────────────────────── */}
            <div className="flex-1 flex flex-col min-w-0 border-r border-gray-200">
              {/* Tab bar: Email + one tab per attachment (underline tabs, matching the site page).
                  Wraps rather than running on: as one row, the fourth and later
                  invoices on an email slid under the assign panel, out of reach. */}
              <div data-testid="email-preview-tabs" className="flex flex-wrap items-end gap-x-0.5 px-3 pt-1 border-b border-gray-200 bg-white shrink-0">
                <button
                  data-testid="email-preview-tab-email"
                  onClick={() => setShowEmailBody(true)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${
                    showEmailBody
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-transparent text-muted-foreground hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Mail className="h-4 w-4" />
                  Email
                </button>
                {detail.attachments.map((a) => (
                  <button
                    key={a.id}
                    data-testid="email-preview-tab-attachment"
                    data-attachment-id={a.id}
                    onClick={() => { setActiveAttachmentId(a.id); setShowEmailBody(false); }}
                    title={`${a.filename}${a.size_bytes != null ? ` (${fmtSize(a.size_bytes)})` : ''}`}
                    className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px max-w-[220px] ${
                      !showEmailBody && activeAttachmentId === a.id
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-muted-foreground hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {detail.attachments.length === 1 ? 'Invoice' : a.filename.replace(/\.pdf$/i, '')}
                    </span>
                  </button>
                ))}
                {!showEmailBody && attachmentUrl && (
                  <a
                    data-testid="email-preview-download"
                    href={attachmentUrl}
                    download={activeAttachment?.filename ?? 'attachment'}
                    className="ml-auto mb-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  >
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                )}
              </div>

              {/* Content area — email body or invoice PDF, full height */}
              <div className="flex-1 min-h-0 bg-gray-100 flex items-center justify-center">
                {showEmailBody ? (
                  detail.bodyHtml ? (
                    <iframe
                      srcDoc={detail.bodyHtml}
                      sandbox=""
                      title="Email body"
                      className="w-full h-full bg-white"
                    />
                  ) : (
                    <pre className="w-full h-full text-xs text-gray-700 whitespace-pre-wrap p-4 overflow-y-auto bg-white">
                      {detail.bodyText || '(no message content)'}
                    </pre>
                  )
                ) : detail.attachments.length === 0 ? (
                  <p data-testid="email-preview-no-attachments" className="text-sm text-gray-400">No attachments on this email</p>
                ) : attachmentLoading ? (
                  <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                ) : attachmentError ? (
                  <p data-testid="email-preview-attachment-error" className="text-sm text-red-600 px-4 text-center">{attachmentError}</p>
                ) : attachmentUrl && isBrowserRenderable(attachmentMime) ? (
                  <iframe data-testid="email-preview-pdf" src={attachmentUrl} title="Invoice preview" className="w-full h-full" />
                ) : attachmentUrl ? (
                  // Framing a non-renderable type (Word, mostly) makes the browser
                  // download it instead — offer the download explicitly instead.
                  <div className="flex flex-col items-center gap-3 text-center px-6">
                    <FileText className="h-10 w-10 text-gray-300" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {describeAttachmentType(attachmentMime)} can't be previewed in the browser
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Download it to read the billing period, then assign it on the right.
                      </p>
                    </div>
                    <a
                      href={attachmentUrl}
                      download={activeAttachment?.filename ?? 'attachment'}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-blue-600 hover:bg-gray-50"
                    >
                      <Download className="h-4 w-4" />
                      Download {activeAttachment?.filename ?? 'attachment'}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>

            {/* ── Right: assign panel ───────────────────────────────────── */}
            <div className="w-[380px] shrink-0 overflow-y-auto p-4 space-y-4">

              {alreadyAssigned && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 space-y-2">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">Assigned</span>
                    {detail.site && (
                      <>
                        {' — '}
                        <Link
                          to={`/bill-validation/sites/${detail.site.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 underline hover:text-green-900"
                        >
                          {detail.site.nmi ?? detail.site.name ?? 'site'} <ExternalLink className="h-3 w-3" />
                        </Link>
                      </>
                    )}
                    {detail.bill && <> · {new Date(detail.bill.bill_from).toLocaleDateString('en-AU')} – {new Date(detail.bill.bill_to).toLocaleDateString('en-AU')}</>}
                  </p>
                  {(parseFailed || parsePending) ? (
                    <div className="space-y-1.5">
                      <p className={`text-xs ${parseFailed ? 'text-red-700' : 'text-amber-700'}`}>
                        {parseFailed ? 'Parsing failed — ' : 'Not parsed yet — '}
                        parse it from the site's <span className="font-medium">Invoices</span> tab
                        (with the PDF review) or the Bill Run's Invoice tab.
                      </p>
                      {/* The REASON, not just the fact. It was stored on the
                          attachment all along and fetched with it, but never
                          rendered — so "Parsing failed" was the whole story a
                          person could get without database access, and every
                          diagnosis started from scratch. */}
                      {parseErrors.map(({ id, filename, error }) => (
                        <p key={id} data-testid="email-preview-parse-error" className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1 break-words">
                          <span className="font-medium">{filename}:</span> {error}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p data-testid="email-preview-parsed-ok" className="text-xs text-green-700">Invoice parsed ✓</p>
                  )}
                </div>
              )}

              {/* Several PDFs on one email are usually several invoices, each for
                  its own NMI and period — one assignment cannot be right for
                  all of them. Splitting makes each its own item. */}
              {!alreadyAssigned && (!detail.source || detail.source === 'gmail') && pdfCount >= 2 && (
                <div data-testid="email-preview-split" className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 space-y-2">
                  <p className="text-xs text-blue-900">
                    <span className="font-medium">{pdfCount} PDFs on this email.</span>{' '}
                    If they are separate invoices, split them: each becomes its own inbox item,
                    matched to its own NMI, assigned and parsed. A covering letter stays here.
                  </p>
                  <Button data-testid="email-preview-split-btn" size="sm" variant="outline" className="w-full gap-1.5 bg-white" disabled={splitting} onClick={() => void handleSplit()}>
                    {splitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                    {splitting ? 'Reading each invoice…' : 'Split into separate invoices'}
                  </Button>
                </div>
              )}
              {splitNotice && (
                <p data-testid="email-preview-split-notice" className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">{splitNotice}</p>
              )}

              {/* Why this email is still here.
                  An invoice the AI read completely assigns and parses itself at
                  ingest, so anything sitting in the inbox is waiting on a
                  detail it could not resolve. Saying which one turns "why has
                  nothing happened" into a single thing to fix. */}
              {heldReasons.length > 0 && !alreadyAssigned && (
                <div data-testid="email-preview-held" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-1.5">
                  <p className="text-xs font-medium text-amber-900">Waiting for your input</p>
                  <ul className="text-xs text-amber-800 space-y-0.5 list-disc pl-4">
                    {heldReasons.map((r) => <li key={r} data-testid="email-preview-held-reason" data-reason={r}>{HELD_REASON_TEXT[r] ?? r}</li>)}
                  </ul>
                  {aiFound.length > 0 && (
                    <p className="text-xs text-amber-700 pt-0.5">
                      Read from the invoice: {aiFound.join(' · ')}
                    </p>
                  )}
                </div>
              )}

              {!isMultiSection && (
              <>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {alreadyAssigned ? 'Re-assign to a different site / bill' : 'Assign to site & bill'}
                  </p>

                  {/* Detected-NMI suggestions */}
                  {nmiSuggestions.length > 0 && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-gray-500">
                        Detected in this email
                        {/* Which path produced these. A row with no extraction
                            was indexed by the old text-matching sweep, whose
                            false positives (account numbers, reference codes)
                            are exactly what the AI pass removes — and it will
                            keep showing them until the email is re-indexed. */}
                        {!extraction && (
                          <span className="ml-1 text-gray-400">· by text matching</span>
                        )}
                      </Label>
                      {/* The way out of the old values. Without this the only
                          way to get AI onto an existing email was to re-add it
                          through the Gmail search screen. */}
                      {!extraction && (
                        <button
                          type="button"
                          data-testid="email-preview-reindex"
                          onClick={handleReindex}
                          disabled={reindexing}
                          className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-800 hover:bg-blue-100 disabled:opacity-60"
                        >
                          {reindexing
                            ? <><Loader2 className="h-3 w-3 animate-spin" /> Re-reading the invoice…</>
                            : <><Zap className="h-3 w-3" /> Re-read this invoice with AI</>}
                        </button>
                      )}
                      <div className="space-y-1.5">
                        {nmiSuggestions.map(({ nmi, site }) => (
                          site ? (
                            <button
                              key={nmi}
                              type="button"
                              data-testid="email-preview-nmi-suggestion"
                              data-nmi={nmi}
                              onClick={() => pickSuggestedSite(site)}
                              className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                                siteId === site.id
                                  ? 'border-blue-300 bg-blue-50'
                                  : 'border-gray-200 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <Zap className="h-4 w-4 text-primary shrink-0" />
                              <span className="min-w-0">
                                <span className="font-medium text-gray-900 font-mono">{site.nmi ?? nmi}</span>
                                <span className="block text-xs text-gray-500 truncate">
                                  {site.addr_street ?? ''} {site.addr_city ?? ''}
                                </span>
                              </span>
                            </button>
                          ) : (
                            <div key={nmi} data-testid="email-preview-nmi-unmatched" data-nmi={nmi} className="flex items-center gap-2 rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-400">
                              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                              NMI <span className="font-mono">{nmi}</span> — no matching site
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label>Site (NMI)</Label>
                    <Input
                      data-testid="email-preview-site-search"
                      placeholder="Search by NMI or address..."
                      value={siteSearch}
                      onChange={(e) => setSiteSearch(e.target.value)}
                    />
                    <Select value={siteId} onValueChange={(v) => { setSiteId(v); setBillId(''); }}>
                      <SelectTrigger data-testid="email-preview-site-select">
                        <SelectValue placeholder={sitesLoading ? 'Loading sites...' : 'Select a site'} />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.nmi || '(no NMI)'} — {s.addr_street ?? ''} {s.addr_city ?? ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {siteId && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label>Bill period</Label>
                        <button
                          type="button"
                          data-testid="email-preview-new-period"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          onClick={() => {
                            setCreatingBill((v) => {
                              const opening = !v;
                              // Pre-fill from whatever billing period was detected in the
                              // invoice PDF/email text at ingestion (see billEmailSyncService.ts
                              // extractBillingPeriod) — only if the user hasn't typed anything yet.
                              if (opening && !newFrom && !newTo && email?.detected_period_from && email?.detected_period_to) {
                                setNewFrom(email.detected_period_from);
                                setNewTo(email.detected_period_to);
                              }
                              return opening;
                            });
                          }}
                        >
                          <Plus className="h-3 w-3" /> New period
                        </button>
                      </div>

                      {!creatingBill ? (
                        <Select value={billId} onValueChange={setBillId}>
                          <SelectTrigger data-testid="email-preview-bill-select">
                            <SelectValue placeholder={bills.length ? 'Select a bill period' : 'No bill periods yet'} />
                          </SelectTrigger>
                          <SelectContent>
                            {bills.map((b) => (
                              <SelectItem key={b.id} value={b.id}>{fmtPeriod(b)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-gray-400">
                            {email?.detected_period_from && email?.detected_period_to
                              ? 'Detected from the invoice — double-check against the PDF on the left before saving.'
                              : "Couldn't detect a billing period automatically — read it off the invoice PDF on the left."}
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-gray-500">From</Label>
                              <Input data-testid="email-preview-new-from" type="date" value={newFrom} onChange={(e) => setNewFrom(e.target.value)} />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">To</Label>
                              <Input data-testid="email-preview-new-to" type="date" value={newTo} onChange={(e) => setNewTo(e.target.value)} />
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            data-testid="email-preview-create-period"
                            disabled={!newFrom || !newTo || assigning}
                            onClick={handleCreateBill}
                          >
                            {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create bill period'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* The invoice is parsed the moment it is assigned, so the
                      retailer whose prompt drives that has to be chosen here —
                      not left until somebody re-parses later. */}
                  {billId && (
                    <div className="pt-1">
                      <RetailerPromptPicker
                        value={retailerId ?? bills.find((b) => b.id === billId)?.retailer_id ?? null}
                        onChange={setRetailerId}
                        siteRetailerName={sites.find((s) => s.id === siteId)?.current_retailer ?? null}
                        disabled={assigning}
                      />
                    </div>
                  )}

                  {assignError && <p className="text-sm text-red-600">{assignError}</p>}
              </>
              )}

              {/* ── Consolidated invoice: one row per site it covers ─────────── */}
              {isMultiSection && (
                <>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Consolidated invoice — assign every site
                  </p>

                  {sectionsResolving ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Matching this invoice's sites…
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500">
                        {resolvedSectionCount} of {sectionRows.length} site
                        {sectionRows.length === 1 ? '' : 's'} on this invoice matched a site.
                      </p>

                      <div className="space-y-2">
                        {sectionRows.map((row, i) => (
                          <div
                            key={`${row.nmi}-${i}`}
                            className={`rounded-lg border px-3 py-2.5 space-y-2 ${
                              row.siteId ? 'border-gray-200 bg-white' : 'border-dashed border-amber-200 bg-amber-50/50'
                            }`}
                          >
                            <div className="flex items-center gap-2 text-sm">
                              {row.siteId ? (
                                <Zap className="h-4 w-4 text-primary shrink-0" />
                              ) : (
                                <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                              )}
                              <span className="min-w-0">
                                <span className="font-medium text-gray-900 font-mono">{row.site?.nmi ?? row.nmi}</span>
                                <span className="block text-xs text-gray-500 truncate">
                                  {row.site
                                    ? `${row.site.addr_street ?? ''} ${row.site.addr_city ?? ''}`.trim() || 'matched site'
                                    : 'no matching site'}
                                </span>
                              </span>
                            </div>
                            {row.siteId && (
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs text-gray-500">From</Label>
                                  <Input
                                    type="date"
                                    value={row.from}
                                    onChange={(e) =>
                                      setSectionRows((prev) =>
                                        prev.map((r, j) => (j === i ? { ...r, from: e.target.value } : r)),
                                      )
                                    }
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">To</Label>
                                  <Input
                                    type="date"
                                    value={row.to}
                                    onChange={(e) =>
                                      setSectionRows((prev) =>
                                        prev.map((r, j) => (j === i ? { ...r, to: e.target.value } : r)),
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {assignError && <p className="text-sm text-red-600">{assignError}</p>}
                    </>
                  )}
                </>
              )}

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                <Button data-testid="email-preview-close" variant="outline" onClick={onClose}>Close</Button>
                {isMultiSection ? (
                  <Button
                    data-testid="email-preview-assign-sections"
                    disabled={!sectionRows.some((r) => r.siteId && r.from && r.to) || assigning}
                    onClick={handleAssignSections}
                  >
                    {assigning
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : `Assign ${resolvedSectionCount} site${resolvedSectionCount === 1 ? '' : 's'} & parse`}
                  </Button>
                ) : (
                  <Button data-testid="email-preview-assign" disabled={!siteId || !billId || assigning} onClick={handleAssign}>
                    {assigning
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : alreadyAssigned ? 'Re-assign & parse' : 'Assign & parse'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
